import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, FileText, Download, Check, X, Trash2, Copy, Send, RefreshCw, Maximize2, Minimize2, Printer } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, ImageRun, AlignmentType, Header, Footer, PageNumber, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import RichTextEditor from '../components/RichTextEditor';
import RecipientSelector from '../components/RecipientSelector';
import { plainTextToHtml, htmlToPlainText } from '../utils/letterHtmlConverter';
import { formatInjurySite } from '../utils/injurySiteFormatter';
import { formatNZDate, normalizeNZDateISO } from '../utils/formatNZDate';
import { useAuth } from '../contexts/AuthContext';
import { useClinic } from '../contexts/ClinicContext';
import API_BASE_URL from '../config/api';

/**
 * Load a base64 signature image onto a white canvas, clean dark border
 * edges, and return as clean PNG base64 string.
 */
function cleanSignatureBackground(base64, type) {
  return new Promise((resolve, reject) => {
    const mimeType = type === 'jpg' ? 'image/jpeg' : 'image/png';
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      // Fill with white first so any transparency becomes white
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      // Clean dark border edges (outer 3px) that come from JPEG artifacts
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const w = canvas.width;
      const h = canvas.height;
      const border = 6;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (x < border || x >= w - border || y < border || y >= h - border) {
            const i = (y * w + x) * 4;
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
            data[i + 3] = 255;
          }
        }
      }
      ctx.putImageData(imageData, 0, 0);
      // Export as PNG base64 (strip data URL prefix)
      const pngDataUrl = canvas.toDataURL('image/png');
      const pngBase64 = pngDataUrl.replace(/^data:image\/png;base64,/, '');
      resolve(pngBase64);
    };
    img.onerror = reject;
    img.src = `data:${mimeType};base64,${base64}`;
  });
}

/**
 * Radiology provider options for MRI referral routing.
 * Keys are sent to the backend, which holds the canonical email addresses
 * (so we can update emails server-side without redeploying the frontend).
 */
const RADIOLOGY_PROVIDERS = [
  { key: 'pacific',      name: 'Pacific Radiology' },
  { key: 'imed',         name: 'I-Med (Waikato)' },
  { key: 'imed_rotorua', name: 'I-Med (Rotorua)' },
  { key: 'river',        name: 'River Radiology' },
  { key: 'beyond',       name: 'Beyond Radiology' },
  { key: 'canopy',       name: 'Canopy Imaging' },
  { key: 'nzmi',         name: 'NZMI (Hamilton)' },
  { key: 'bay',          name: 'Bay Radiology' },
];

// Returns the best guess at the actual referrer's name for display.
// The "Dear X," line in the letter body has been resolved by the stricter
// letterGenerator prompt and is preferred. The raw Referral fields can be
// contaminated with the original addressee, so they're only used as fallback,
// or when they happen to contain the body's name (gives us the full
// "First Last, Practice" string).
function resolveReferrerDisplay(referral, letterContent) {
  const fromExtraction = ((referral?.referrerName) || (referral?.gpName) || '').trim();
  const m = (letterContent || '').match(/^\s*Dear\s+(.+?)\s*,/m);
  const fromBody = m ? m[1].trim() : '';
  if (!fromBody) return fromExtraction;
  const bodyFirstName = fromBody.split(/\s+/).slice(-1)[0].toLowerCase();
  if (bodyFirstName && fromExtraction.toLowerCase().includes(bodyFirstName)) {
    return fromExtraction; // extraction agrees with the body — use the longer form
  }
  return fromBody; // extraction disagrees — body wins
}

// Editable text cell used inside the PatientInfoBlock table. Looks like plain
// bold text until clicked. Calls onSave on blur if the value has actually
// changed; brief background flash signals the result.
// `display`, when provided, is shown while the cell is NOT being edited (e.g. the
// formatted "Left Knee"); focusing the cell reveals the raw `value` for editing.
function EditableCell({ value, onSave, type = 'text', display, placeholder }) {
  const [local, setLocal] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [focused, setFocused] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'ok' | 'err' | null
  const originalRef = useRef(value ?? '');

  useEffect(() => {
    setLocal(value ?? '');
    originalRef.current = value ?? '';
  }, [value]);

  const handleBlur = async () => {
    setFocused(false);
    const trimmed = (local || '').trim();
    if (trimmed === (originalRef.current || '').trim()) return;
    setSaving(true);
    try {
      await onSave(trimmed);
      originalRef.current = trimmed;
      setFeedback('ok');
      setTimeout(() => setFeedback(null), 800);
    } catch (err) {
      setLocal(originalRef.current);
      setFeedback('err');
      alert(err.response?.data?.error || err.message || 'Failed to save');
      setTimeout(() => setFeedback(null), 1500);
    } finally {
      setSaving(false);
    }
  };

  const bg = feedback === 'ok' ? '#dcfce7'
    : feedback === 'err' ? '#fee2e2'
    : 'transparent';

  return (
    <input
      type={type}
      value={(!focused && display != null) ? display : local}
      placeholder={placeholder || ''}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={handleBlur}
      disabled={saving}
      style={{
        width: '100%', border: 'none', outline: 'none',
        padding: '1px 4px', backgroundColor: bg,
        fontWeight: 700, color: '#111827',
        fontFamily: 'inherit', fontSize: 'inherit',
        borderRadius: 3,
        transition: 'background-color 0.2s'
      }}
      onFocus={(e) => { setFocused(true); e.target.style.backgroundColor = '#f3f4f6'; }}
      onMouseEnter={(e) => { if (document.activeElement !== e.target && !feedback) e.target.style.backgroundColor = '#f9fafb'; }}
      onMouseLeave={(e) => { if (document.activeElement !== e.target && !feedback) e.target.style.backgroundColor = 'transparent'; }}
    />
  );
}

// Patient/specialist info block shown at the top of consultation letters in the
// viewer. Cells are inline-editable: patient fields save to /patient-files/:id,
// referral fields save to /referrals/:id. Hardcoded specialist/facility cells
// and the derived Date of Assessment stay read-only.
function PatientInfoBlock({ patient, letter, onPatientChange, onReferralChange }) {
  if (!patient || !letter) return null;
  const referral = letter.referral || {};

  // Day-first aware: stored DOB/injury dates are DD/MM/YYYY, which new Date()
  // would misread as US MM/DD. See utils/formatNZDate.
  const formatDate = formatNZDate;

  const assessmentDate = formatDate(patient.selectedSlot || letter.generatedAt);

  // Best-guess Referrer pulled from the letter body's "Dear X," — same logic
  // as before. The editable value here is referral.referrerName so the user can
  // override the auto-resolved value if both sources are wrong.
  const referrerDisplay = resolveReferrerDisplay(referral, letter.content);

  // Patient field save — PUT /patient-files/:id
  const savePatientField = (field) => async (value) => {
    const res = await axios.put(`${API_BASE_URL}/patient-files/${patient.id}`, { [field]: value });
    if (onPatientChange) onPatientChange(res.data);
  };

  // Referral field save — PUT /referrals/:id
  const saveReferralField = (field) => async (value) => {
    if (!referral.id) throw new Error('No referral linked to this letter — edit the referral on the patient file instead.');
    const res = await axios.put(`${API_BASE_URL}/referrals/${referral.id}`, { [field]: value });
    if (onReferralChange) onReferralChange(res.data);
  };

  const labelStyle = { padding: '2px 8px 2px 0', fontWeight: 700, color: '#111827', verticalAlign: 'top', whiteSpace: 'nowrap' };
  const valueStyle = { padding: '2px 8px 2px 0', fontWeight: 700, color: '#111827', verticalAlign: 'top' };
  const readOnly = (v) => <span style={{ padding: '1px 4px' }}>{v || ''}</span>;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>
      <colgroup>
        <col style={{ width: '20%' }} />
        <col style={{ width: '26%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '34%' }} />
      </colgroup>
      <tbody>
        <tr>
          <td style={labelStyle}>Claimant Name:</td>
          <td style={valueStyle}><EditableCell value={patient.name} onSave={savePatientField('name')} /></td>
          <td style={labelStyle}>Specialist Name:</td>
          <td style={valueStyle}>{readOnly('Dr Stuart Armstrong')}</td>
        </tr>
        <tr>
          <td style={labelStyle}>Date of Birth:</td>
          <td style={valueStyle}><EditableCell value={formatDate(patient.dateOfBirth)} onSave={savePatientField('dateOfBirth')} /></td>
          <td style={labelStyle}>NZMC No:</td>
          <td style={valueStyle}>{readOnly('36701')}</td>
        </tr>
        <tr>
          <td style={labelStyle}>NHI No:</td>
          <td style={valueStyle}><EditableCell value={patient.nhi} onSave={savePatientField('nhi')} /></td>
          <td style={labelStyle}>Date of Assessment:</td>
          <td style={valueStyle}>{readOnly(assessmentDate)}</td>
        </tr>
        <tr>
          <td style={labelStyle}>Address:</td>
          <td style={valueStyle}><EditableCell value={patient.address} onSave={savePatientField('address')} /></td>
          <td style={labelStyle}>Facility:</td>
          <td style={valueStyle}>{readOnly('Alpha St Clinic, CAMBRIDGE')}</td>
        </tr>
        <tr>
          <td style={labelStyle}>Telephone No:</td>
          <td style={valueStyle}><EditableCell value={patient.phone} onSave={savePatientField('phone')} /></td>
          <td style={labelStyle}>Referrer:</td>
          <td style={valueStyle}>
            <EditableCell value={referral.referrerName || referrerDisplay} onSave={saveReferralField('referrerName')} />
            <EditableCell value={referral.referrerClinic || ''} placeholder="Add clinic…" onSave={saveReferralField('referrerClinic')} />
          </td>
        </tr>
        <tr>
          <td style={labelStyle}>Email Address:</td>
          <td style={valueStyle}><EditableCell value={patient.email} onSave={savePatientField('email')} /></td>
          <td style={labelStyle}>Consultation Type:</td>
          <td style={valueStyle}>{readOnly('Initial Simple')}</td>
        </tr>
        <tr>
          <td style={labelStyle}>Injury Site:</td>
          <td style={valueStyle}><EditableCell value={referral.injuryType} display={formatInjurySite(referral.injuryType, letter.form?.clinicalData?.injuredSide)} onSave={saveReferralField('injuryType')} /></td>
          <td style={labelStyle}>ACC Claim No:</td>
          <td style={valueStyle}><EditableCell value={patient.accNumber} onSave={savePatientField('accNumber')} /></td>
        </tr>
        <tr>
          <td style={labelStyle}>Date of Injury:</td>
          <td style={valueStyle}><EditableCell value={formatDate(referral.injuryDate)} onSave={saveReferralField('injuryDate')} /></td>
          <td style={labelStyle}></td>
          <td style={valueStyle}></td>
        </tr>
      </tbody>
    </table>
  );
}

function ordinalSuffix(n) {
  const s = n % 100;
  if (s >= 11 && s <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function formatDayHeading(date) {
  const weekday = date.toLocaleDateString('en-NZ', { weekday: 'long' });
  const day = date.getDate();
  const month = date.toLocaleDateString('en-NZ', { month: 'long' });
  return `${weekday} ${day}${ordinalSuffix(day)} ${month} ${date.getFullYear()}`;
}

function base64ToUint8Array(base64) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function LettersPage() {
  const [letters, setLetters] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [retryingGensolve, setRetryingGensolve] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [radiologyProvider, setRadiologyProvider] = useState('');
  const [emailingRadiology, setEmailingRadiology] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeClinicId } = useClinic();
  const isDoctor = user?.role === 'doctor' || user?.role === 'admin';

  // Group the already-sorted letters by appointment day (NZ local). Letters
  // with no booked slot go into a single trailing group.
  const groupedLetters = useMemo(() => {
    const groups = [];
    let current = null;
    for (const letter of letters) {
      const slot = letter.patient?.selectedSlot ? new Date(letter.patient.selectedSlot) : null;
      const key = slot ? slot.toLocaleDateString('en-NZ') : '__no_slot__';
      const label = slot ? formatDayHeading(slot) : 'No appointment scheduled';
      if (!current || current.key !== key) {
        current = { key, label, letters: [] };
        groups.push(current);
      }
      current.letters.push(letter);
    }
    return groups;
  }, [letters]);

  useEffect(() => {
    fetchLetters();
  }, [activeClinicId]);

  // Open a specific letter when arriving with ?letterId=... in the URL
  // (e.g. clicking the eye icon from a patient's Letters tab). Defaults
  // to edit mode for doctors, matching the list-click flow.
  useEffect(() => {
    const targetId = searchParams.get('letterId');
    if (targetId && (!selectedLetter || selectedLetter.letter?.id !== targetId)) {
      viewLetter(targetId);
      const next = new URLSearchParams(searchParams);
      next.delete('letterId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams]);

  // Open a letter for the patient when arriving with ?patientId=...
  // (e.g. clicking an appointment in the diary). Prefers the consultation
  // letter (any status) over an imaging/MRI referral; if there's no
  // consultation letter, falls back to the most-recent editable letter,
  // then to the patient file's Letters tab.
  useEffect(() => {
    const patientId = searchParams.get('patientId');
    if (!patientId || loading) return;
    const byNewest = (a, b) => new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0);
    const patientLetters = letters.filter(l => l.patientId === patientId).sort(byNewest);
    const consultation = patientLetters.find(l => l.letterType !== 'mri_referral');
    const editable = patientLetters.filter(l => !['sent', 'ready_to_send'].includes(l.status));
    const candidate = consultation || editable[0];
    if (candidate) {
      viewLetter(candidate.id);
      const next = new URLSearchParams(searchParams);
      next.delete('patientId');
      setSearchParams(next, { replace: true });
    } else {
      navigate(`/patient-file/${patientId}?tab=letters`);
    }
  }, [searchParams, letters, loading]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && fullscreen) setFullscreen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [fullscreen]);

  const fetchLetters = async () => {
    try {
      const clinicQuery = activeClinicId ? `?clinicId=${activeClinicId}` : '';
      const response = await axios.get(`${API_BASE_URL}/letters${clinicQuery}`);
      const raw = response.data.data || response.data;
      // Order by patient appointment time (selectedSlot) DESC so the next
      // upcoming appointment sits at the top of the list and the doctor can
      // work down toward the earlier ones during the day. Letters with no
      // booked slot fall to the bottom, then ordered by generatedAt DESC.
      const sorted = [...raw].sort((a, b) => {
        const aSlot = a.patient?.selectedSlot ? new Date(a.patient.selectedSlot).getTime() : null;
        const bSlot = b.patient?.selectedSlot ? new Date(b.patient.selectedSlot).getTime() : null;
        if (aSlot !== null && bSlot !== null) return bSlot - aSlot;
        if (aSlot !== null) return -1;
        if (bSlot !== null) return 1;
        return new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0);
      });
      setLetters(sorted);
    } catch (error) {
      console.error('Error fetching letters:', error);
    } finally {
      setLoading(false);
    }
  };

  // Merge updates from inline edits in the patient info block back into the
  // selected letter state, so the table reflects saved values without a full
  // re-fetch.
  const handlePatientChange = (updatedPatient) => {
    setSelectedLetter(prev => prev ? { ...prev, patient: { ...prev.patient, ...updatedPatient } } : prev);
  };
  const handleReferralChange = (updatedReferral) => {
    setSelectedLetter(prev => {
      if (!prev) return prev;
      return { ...prev, letter: { ...prev.letter, referral: { ...prev.letter.referral, ...updatedReferral } } };
    });
  };

  const viewLetter = async (letterId, autoEdit) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/letters/${letterId}`);
      // Strip any literal markdown bold markers that older letters may have
      // baked into their stored HTML so they don't render as **like this**.
      if (response.data?.letter?.contentHtml) {
        response.data.letter.contentHtml = response.data.letter.contentHtml.replace(/\*\*/g, '');
      }
      setSelectedLetter(response.data);
      // Use contentHtml if available, otherwise convert plain text
      const html = response.data.letter.contentHtml || plainTextToHtml(response.data.letter.content);
      setEditedHtml(html);
      setCopied(false);
      setRadiologyProvider(response.data.letter.radiologyProvider || '');
      // Doctor/admin auto-enters edit mode on editable letters
      const canEdit = !['sent', 'ready_to_send'].includes(response.data.letter.status);
      if (autoEdit !== false) setEditing(isDoctor && canEdit);
    } catch (error) {
      console.error('Error fetching letter:', error);
    }
  };

  const regenerateLetter = async () => {
    if (!selectedLetter?.letter?.id) return;
    if (!window.confirm('Regenerate this letter? Current content will be replaced.')) return;
    setRegenerating(true);
    try {
      await axios.post(
        `${API_BASE_URL}/letters/${selectedLetter.letter.id}/regenerate`,
        {}
      );
      // Re-fetch so the new content shows up; keep the user out of edit mode so they can review
      await viewLetter(selectedLetter.letter.id, false);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to regenerate letter.';
      alert(msg);
    } finally {
      setRegenerating(false);
    }
  };

  const emailToRadiology = async () => {
    if (!radiologyProvider) {
      alert('Please choose a radiology provider first.');
      return;
    }
    if (!selectedLetter?.letter?.id) return;
    setEmailingRadiology(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/letters/${selectedLetter.letter.id}/email-radiology`,
        { provider: radiologyProvider }
      );
      alert(`Sent to ${res.data?.providerName || 'provider'} (${res.data?.email || ''}).`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send to radiology provider.';
      alert(msg);
    } finally {
      setEmailingRadiology(false);
    }
  };

  // Combined "Save & Approve" used by both the inline and fullscreen editors.
  // For MRI letters where a radiology provider has been selected, also emails
  // the approved letter to the provider in the same action so reception doesn't
  // have to click an extra button. Done inline (not via updateStatus) so we
  // produce one combined alert rather than two.
  const handleSaveAndApprove = async () => {
    if (!selectedLetter?.letter?.id) return;
    try {
      const plainText = htmlToPlainText(editedHtml);
      await axios.put(`${API_BASE_URL}/letters/${selectedLetter.letter.id}/content`, {
        content: plainText,
        contentHtml: editedHtml
      });
      setEditing(false);
      setFullscreen(false);
      // Approve (also triggers Gensolve upload of the approved PDF server-side)
      await axios.put(`${API_BASE_URL}/letters/${selectedLetter.letter.id}/status`, { status: 'approved' });

      let emailMsg = '';
      const isMRI = selectedLetter.letter.letterType === 'mri_referral';
      if (isMRI && radiologyProvider) {
        try {
          const res = await axios.post(
            `${API_BASE_URL}/letters/${selectedLetter.letter.id}/email-radiology`,
            { provider: radiologyProvider }
          );
          emailMsg = ` Sent to ${res.data?.providerName || 'provider'} (${res.data?.email || ''}).`;
        } catch (emailErr) {
          emailMsg = ` But failed to email provider: ${emailErr.response?.data?.error || emailErr.message}`;
        }
      }

      alert(`Letter approved.${emailMsg}`);
      viewLetter(selectedLetter.letter.id, false);
      fetchLetters();
    } catch (error) {
      alert('Failed to save and approve letter');
    }
  };

  const saveEdit = async () => {
    try {
      // Save both HTML and plain text versions
      const plainText = htmlToPlainText(editedHtml);
      await axios.put(`${API_BASE_URL}/letters/${selectedLetter.letter.id}/content`, {
        content: plainText,
        contentHtml: editedHtml
      });
      setEditing(false);
      setFullscreen(false);
      alert('Letter updated successfully');
      viewLetter(selectedLetter.letter.id, false);
    } catch (error) {
      alert('Failed to update letter');
    }
  };

  // Save the chosen radiology provider on the letter so it persists across
  // reloads and gets used by the Gensolve upload PDF (which prepends
  // "To: [Provider]" so reception can see where the MRI was sent).
  const handleRadiologyProviderChange = async (value) => {
    setRadiologyProvider(value);
    if (!selectedLetter?.letter?.id) return;
    try {
      await axios.put(
        `${API_BASE_URL}/letters/${selectedLetter.letter.id}/radiology-provider`,
        { provider: value || null }
      );
    } catch (err) {
      console.error('Failed to save radiology provider:', err);
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API_BASE_URL}/letters/${selectedLetter.letter.id}/status`, {
        status
      });
      alert('Letter status updated');
      setEditing(false);
      viewLetter(selectedLetter.letter.id, false);
      fetchLetters();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleReadyToSend = () => {
    setShowRecipientSelector(true);
  };

  const confirmReadyToSend = async (recipients) => {
    try {
      await axios.put(`${API_BASE_URL}/letters/${selectedLetter.letter.id}/ready-to-send`, {
        recipients: recipients.map(r => ({
          contactId: r.contactId || null,
          email: r.email,
          name: r.name,
          recipientType: r.recipientType
        }))
      });
      setShowRecipientSelector(false);
      alert('Letter marked ready to send');
      viewLetter(selectedLetter.letter.id, false);
      fetchLetters();
    } catch (error) {
      alert('Failed to mark letter ready to send');
    }
  };

  const deleteLetter = async () => {
    if (!window.confirm('Are you sure you want to delete this letter? This cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/letters/${selectedLetter.letter.id}`);
      alert('Letter deleted successfully');
      setSelectedLetter(null);
      fetchLetters();
    } catch (error) {
      console.error('Error deleting letter:', error);
      alert('Failed to delete letter');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(selectedLetter.letter.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const retryGensolveUpload = async (letterId) => {
    setRetryingGensolve(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/outbox/retry-gensolve/${letterId}`);
      if (response.data.gensolveUploadStatus === 'uploaded') {
        alert('Successfully uploaded to Gensolve');
      } else {
        alert(`Gensolve upload status: ${response.data.gensolveUploadStatus}`);
      }
      viewLetter(letterId, false);
      fetchLetters();
    } catch (error) {
      console.error('Error retrying Gensolve upload:', error);
      alert('Failed to retry Gensolve upload');
    } finally {
      setRetryingGensolve(false);
    }
  };

  const downloadText = () => {
    const blob = new Blob([selectedLetter.letter.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `letter-${selectedLetter.patient.name}-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Fetch the user's signature image as a clean PNG data URL, or null if
   * unavailable. Used by Print / Save-as-PDF for MRI referrals so the
   * signature is embedded automatically (matches the Word export path).
   */
  const fetchSignatureDataUrl = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/letters/signature/image`);
      const rawBase64 = res?.data?.signature;
      if (!rawBase64) return null;
      const rawType = res.data.type || (rawBase64.startsWith('/9j/') ? 'jpg' : 'png');
      try {
        const cleanedPng = await cleanSignatureBackground(rawBase64, rawType);
        return `data:image/png;base64,${cleanedPng}`;
      } catch {
        return `data:image/${rawType === 'jpg' ? 'jpeg' : 'png'};base64,${rawBase64}`;
      }
    } catch {
      return null;
    }
  };

  /**
   * Inject the signature image after the "Signature:" line in MRI HTML.
   * Looks for the literal string "Signature:" inside any element and inserts
   * an <img> immediately after it. Falls back to unchanged HTML if not found.
   */
  const injectMriSignatureHtml = (html, signatureDataUrl) => {
    if (!signatureDataUrl) return html;
    const imgTag = `<img src="${signatureDataUrl}" alt="Signature" style="display:inline-block;vertical-align:middle;width:90px;height:auto;margin:0 0 0 4px;" />`;
    // Replace "Signature:" + any blank line markers (underscores) with the label + signature image
    return html.replace(/(Signature:)\s*_+\s*/i, `$1 ${imgTag} `).replace(
      // Fallback: if no underscore placeholder, still inject the image directly after the colon
      /(Signature:)(?!\s*<img)/i,
      `$1 ${imgTag} `
    );
  };

  const writePrintDocument = (printWindow, content, title, compact = false) => {
    // MRI radiology requests must fit on a single page — use a smaller font,
    // tighter line spacing and narrower margins than a standard letter.
    const bodyStyle = compact
      ? 'font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #111827; margin: 28px;'
      : 'font-family: Arial, sans-serif; font-size: 14px; line-height: 1.8; color: #111827; margin: 40px;';
    // Note: don't override @page margins for the compact form — letting the
    // browser keep its printer-safe default avoids clipping content at the
    // page edges. The smaller font + tighter spacing is what makes it fit.
    const extraStyle = compact
      ? `body > *, p, div, ul, h1, h2, h3, h4 { margin-top: 0; margin-bottom: 6px; }
            h3 { font-size: 12px; }
            ul { padding-left: 18px; }
            li { margin-bottom: 2px; }
            p:empty { margin: 0; height: 6px; line-height: 6px; }
            @media print { body { margin: 16px; } }`
      : `@media print { body { margin: 20px; } }`;
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { ${bodyStyle} }
            ${extraStyle}
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    // Wait for any embedded images (e.g. the signature data URL) to finish
    // loading before opening the print dialog — otherwise Chrome snapshots the
    // page while the image is still 0×0 and it's missing from the printout.
    const imgs = Array.from(printWindow.document.images || []);
    const pending = imgs
      .filter(img => !img.complete)
      .map(img => new Promise(resolve => { img.onload = img.onerror = resolve; }));
    const triggerPrint = () => printWindow.print();
    if (pending.length === 0) {
      triggerPrint();
    } else {
      // Cap the wait so a stuck image can't block printing forever.
      Promise.race([Promise.all(pending), new Promise(r => setTimeout(r, 3000))]).then(triggerPrint);
    }
  };

  const openPrintWindow = () => {
    // Open the window synchronously inside the click handler so Chrome doesn't
    // block it as a popup — then do any async work and write content after.
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('The print window was blocked by your browser. Please allow pop-ups for this site and try again.');
      return;
    }
    const isMRI = selectedLetter.letter.letterType === 'mri_referral';
    const baseContent = selectedLetter.letter.contentHtml || `<pre style="white-space:pre-wrap;font-family:Arial,sans-serif;font-size:14px;line-height:1.8">${selectedLetter.letter.content}</pre>`;
    const title = `Letter - ${selectedLetter.patient.name}`;

    if (!isMRI) {
      writePrintDocument(printWindow, baseContent, title);
      return;
    }

    // MRI: write a placeholder, then fetch + embed the signature, then re-write.
    printWindow.document.write('<html><body style="font-family:Arial,sans-serif;padding:40px;color:#6b7280">Preparing signed document for print…</body></html>');
    (async () => {
      try {
        const sigUrl = await fetchSignatureDataUrl();
        let content = injectMriSignatureHtml(baseContent, sigUrl);
        const provider = RADIOLOGY_PROVIDERS.find(p => p.key === radiologyProvider);
        if (provider) {
          content = `<p style="margin:0 0 12px 0;font-weight:600;">To: ${provider.name}</p>` + content;
        }
        writePrintDocument(printWindow, content, title, true);
      } catch {
        writePrintDocument(printWindow, baseContent, title, true);
      }
    })();
  };

  const printLetter = () => openPrintWindow();
  const downloadPdf = () => openPrintWindow();

  const downloadWord = async () => {
    try {
      const isMRI = selectedLetter.letter.letterType === 'mri_referral';

      // Fetch signature and letterhead in parallel
      let signatureData = null;
      let signatureType = 'png';
      let headerImageData = null;
      let headerImageType = 'jpg';
      let footerImageData = null;
      let footerImageType = 'jpg';

      const [sigResult, letterheadResult] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/letters/signature/image`),
        !isMRI ? axios.get(`${API_BASE_URL}/letters/letterhead/images`) : Promise.resolve(null)
      ]);

      // Process signature
      if (sigResult.status === 'fulfilled' && sigResult.value?.data?.signature) {
        const rawBase64 = sigResult.value.data.signature;
        const rawType = sigResult.value.data.type || (rawBase64.startsWith('/9j/') ? 'jpg' : 'png');
        try {
          signatureData = await cleanSignatureBackground(rawBase64, rawType);
          signatureType = 'png';
        } catch (cleanErr) {
          console.error('Failed to clean signature, using raw:', cleanErr);
          signatureData = rawBase64;
          signatureType = rawType;
        }
      }

      // Process letterhead (only for non-MRI)
      if (!isMRI && letterheadResult.status === 'fulfilled' && letterheadResult.value?.data) {
        const lhData = letterheadResult.value.data;
        if (lhData.header) {
          headerImageData = lhData.header;
          headerImageType = lhData.headerType || 'jpg';
        }
        if (lhData.footer) {
          footerImageData = lhData.footer;
          footerImageType = lhData.footerType || 'jpg';
        }
      }

      const content = selectedLetter.letter.content;
      const lines = content.split('\n');

      const fontSize = isMRI ? 21 : 24;
      const smallFontSize = isMRI ? 19 : 22;
      const emptyLineSpacing = isMRI ? 60 : 120;
      const headerSpacingBefore = isMRI ? 120 : 240;
      const headerSpacingAfter = isMRI ? 60 : 120;
      const bodySpacingAfter = isMRI ? 60 : 120;

      const children = [];

      // For non-MRI letters, insert borderless patient details table before letter body
      if (!isMRI) {
        const patient = selectedLetter.patient || {};
        const referral = selectedLetter.letter.referral || {};
        const letter = selectedLetter.letter;

        // Day-first aware (DD/MM/YYYY) — see utils/formatNZDate.
        const formatDate = formatNZDate;

        const noBorders = {
          top: { style: BorderStyle.NONE, size: 0 },
          bottom: { style: BorderStyle.NONE, size: 0 },
          left: { style: BorderStyle.NONE, size: 0 },
          right: { style: BorderStyle.NONE, size: 0 }
        };

        const tableFontSize = 20;

        const makeRow = (label1, value1, label2, value2) => {
          return new TableRow({
            children: [
              new TableCell({
                borders: noBorders,
                width: { size: 2000, type: WidthType.DXA },
                children: [new Paragraph({ children: [new TextRun({ text: label1, bold: true, size: tableFontSize })] })]
              }),
              new TableCell({
                borders: noBorders,
                width: { size: 2600, type: WidthType.DXA },
                children: [new Paragraph({ children: [new TextRun({ text: value1 || '', bold: true, size: tableFontSize })] })]
              }),
              new TableCell({
                borders: noBorders,
                width: { size: 2000, type: WidthType.DXA },
                children: [new Paragraph({ children: [new TextRun({ text: label2, bold: true, size: tableFontSize })] })]
              }),
              new TableCell({
                borders: noBorders,
                width: { size: 3500, type: WidthType.DXA },
                // value2 may be an array of lines (e.g. referrer name + clinic),
                // each rendered as its own stacked paragraph within the cell.
                children: (Array.isArray(value2) ? value2 : [value2]).map((line) =>
                  new Paragraph({ children: [new TextRun({ text: line || '', bold: true, size: tableFontSize })] })
                )
              })
            ]
          });
        };

        const referrerDisplay = resolveReferrerDisplay(referral, letter.content);
        const referrerClinic = (referral.referrerClinic || '').trim();
        const referrerCell = referrerClinic ? [referrerDisplay, referrerClinic] : referrerDisplay;

        const patientTable = new Table({
          width: { size: 10100, type: WidthType.DXA },
          borders: {
            top: { style: BorderStyle.NONE, size: 0 },
            bottom: { style: BorderStyle.NONE, size: 0 },
            left: { style: BorderStyle.NONE, size: 0 },
            right: { style: BorderStyle.NONE, size: 0 },
            insideHorizontal: { style: BorderStyle.NONE, size: 0 },
            insideVertical: { style: BorderStyle.NONE, size: 0 }
          },
          rows: [
            makeRow('Claimant Name:', patient.name, 'Specialist Name:', 'Dr Stuart Armstrong'),
            makeRow('Date of Birth:', formatDate(patient.dateOfBirth), 'NZMC No:', '36701'),
            makeRow('NHI No:', patient.nhi, 'Date of Assessment:', formatDate(patient.selectedSlot || letter.generatedAt)),
            makeRow('Address:', patient.address, 'Facility:', 'Alpha St Clinic, CAMBRIDGE'),
            makeRow('Telephone No:', patient.phone, 'Referrer:', referrerCell),
            makeRow('Email Address:', patient.email, 'Consultation Type:', 'Initial Simple'),
            makeRow('Injury Site:', formatInjurySite(referral.injuryType, selectedLetter.letter.form?.clinicalData?.injuredSide), 'ACC Claim No:', patient.accNumber),
            makeRow('Date of Injury:', formatDate(referral.injuryDate), '', '')
          ]
        });

        children.push(patientTable);
        children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
      }

      // For MRI referrals with a selected radiology provider, prepend the "To:" header
      if (isMRI) {
        const provider = RADIOLOGY_PROVIDERS.find(p => p.key === radiologyProvider);
        if (provider) {
          children.push(new Paragraph({
            children: [new TextRun({ text: `To: ${provider.name}`, bold: true, size: fontSize })],
            spacing: { before: 0, after: 120 }
          }));
        }
      }

      lines.forEach((line) => {
        // Strip markdown bold markers so they don't render literally in the doc.
        let trimmedLine = line.trim().replace(/\*\*/g, '');
        const strippedLine = trimmedLine;

        const isHeader = /^[A-Z\s]{3,}:$/.test(strippedLine) ||
                        strippedLine.startsWith('HISTORY:') ||
                        strippedLine.startsWith('EXAMINATION:') ||
                        strippedLine.startsWith('INVESTIGATIONS:') ||
                        strippedLine.startsWith('DIAGNOSIS:') ||
                        strippedLine.startsWith('CAUSATION') ||
                        strippedLine.startsWith('MANAGEMENT:') ||
                        strippedLine.startsWith('EXERCISE VITAL SIGN') ||
                        (isMRI && /^[A-Z\s]{3,}:/.test(strippedLine)) ||
                        (isMRI && strippedLine === 'RADIOLOGY REQUEST FORM');

        if (isHeader) trimmedLine = strippedLine;

        const isDear = trimmedLine.startsWith('Dear ');
        const isYoursSincerely = trimmedLine.startsWith('Yours ');
        const isDrSignature = trimmedLine.startsWith('Dr ');
        const isCredentials = /^(Sports|NZMC|Anglesea|ACC Vendor)/.test(trimmedLine);
        const isCc = trimmedLine.startsWith('cc:');
        const isSignatureLine = isMRI && trimmedLine.startsWith('Signature:');

        if (trimmedLine === '') {
          children.push(new Paragraph({ text: '', spacing: { after: emptyLineSpacing } }));
        } else if (isHeader) {
          children.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, bold: true, size: fontSize })],
            spacing: { before: headerSpacingBefore, after: headerSpacingAfter }
          }));
        } else if (isDear) {
          children.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, size: fontSize })],
            spacing: { before: 240, after: 240 }
          }));
        } else if (isYoursSincerely) {
          children.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, size: fontSize })],
            spacing: { before: 120, after: 60 }
          }));
          if (signatureData) {
            try {
              const sigData = base64ToUint8Array(signatureData);
              children.push(new Paragraph({
                children: [new ImageRun({ data: sigData, transformation: { width: 90, height: 97 }, type: signatureType })],
                spacing: { before: 60, after: 0 }
              }));
            } catch (sigErr) { console.error('Failed to insert signature:', sigErr); }
          }
        } else if (isSignatureLine) {
          children.push(new Paragraph({
            children: [new TextRun({ text: 'Signature: ', size: fontSize })],
            spacing: { before: 60, after: 0 }
          }));
          if (signatureData) {
            try {
              const sigData = base64ToUint8Array(signatureData);
              children.push(new Paragraph({
                children: [new ImageRun({ data: sigData, transformation: { width: 70, height: 75 }, type: signatureType })],
                spacing: { before: 0, after: 0 }
              }));
            } catch (sigErr) { console.error('Failed to insert signature:', sigErr); }
          }
        } else if (isDrSignature || isCredentials) {
          children.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, size: fontSize })],
            spacing: { before: isDrSignature ? 0 : isMRI ? 0 : 60, after: isMRI ? 0 : 60 }
          }));
        } else if (isCc) {
          children.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, size: smallFontSize, italics: true })],
            spacing: { before: isMRI ? 20 : 60, after: isMRI ? 20 : 60 }
          }));
        } else {
          children.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, size: fontSize })],
            spacing: { after: bodySpacingAfter }
          }));
        }
      });

      // Build header and footer based on letter type
      let docHeader, docFooter, topMargin, bottomMargin;

      if (!isMRI && headerImageData) {
        // Consultation letter with letterhead header image (1837x558, full page width)
        const headerImgData = base64ToUint8Array(headerImageData);
        docHeader = new Header({
          children: [new Paragraph({
            children: [new ImageRun({
              data: headerImgData,
              transformation: { width: 595, height: 181 },
              type: headerImageType
            })],
            spacing: { after: 0 }
          })]
        });
        topMargin = 4200; // Accommodate ~181pt header image + spacing
      } else {
        docHeader = new Header({
          children: [new Paragraph({
            children: [new TextRun({ text: isMRI ? 'RADIOLOGY REFERRAL' : 'CONSULTATION LETTER', bold: true, size: 20, color: '666666' })],
            alignment: AlignmentType.RIGHT
          })]
        });
        topMargin = 1440;
      }

      if (!isMRI && footerImageData) {
        // Consultation letter with letterhead footer image (2480x450 cropped, full page width)
        const footerImgData = base64ToUint8Array(footerImageData);
        docFooter = new Footer({
          children: [new Paragraph({
            children: [new ImageRun({
              data: footerImgData,
              transformation: { width: 595, height: 108 },
              type: footerImageType
            })],
            spacing: { before: 0 }
          })]
        });
        bottomMargin = 2800; // Accommodate ~108pt footer image + spacing
      } else {
        docFooter = new Footer({
          children: [new Paragraph({
            children: [
              new TextRun({ text: 'Page ', size: 18, color: '666666' }),
              new TextRun({ children: [PageNumber.CURRENT], size: 18, color: '666666' }),
              new TextRun({ text: ' of ', size: 18, color: '666666' }),
              new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 18, color: '666666' })
            ],
            alignment: AlignmentType.CENTER
          })]
        });
        bottomMargin = 1440;
      }

      const doc = new Document({
        sections: [{
          properties: { page: { margin: { top: topMargin, right: 1440, bottom: bottomMargin, left: 1440 } } },
          headers: { default: docHeader },
          footers: { default: docFooter },
          children
        }]
      });

      const blob = await Packer.toBlob(doc);
      const filePrefix = isMRI ? 'mri-referral' : 'letter';
      const fileName = `${filePrefix}-${selectedLetter.patient.name}-${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error generating Word document:', error);
      alert('Failed to generate Word document. Downloading as text instead.');
      downloadText();
    }
  };

  const getGensolveBadge = (status) => {
    if (!status) return null;
    const styles = {
      uploaded: { bg: '#dcfce7', text: '#166534', label: 'In Gensolve' },
      failed: { bg: '#fee2e2', text: '#991b1b', label: 'Gensolve Failed' },
      skipped: { bg: '#f3f4f6', text: '#6b7280', label: 'No Gensolve Link' }
    };
    return styles[status] || null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'reviewed':
        return { bg: '#dcfce7', text: '#166534' };
      case 'pending_review':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'ready_to_send':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'sent':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'rejected':
        return { bg: '#fee2e2', text: '#991b1b' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '48px', height: '48px',
          border: '4px solid #e5e7eb', borderTop: '4px solid #2563eb',
          borderRadius: '50%', animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#111827', marginBottom: '32px' }}>
          Documents & Letters
        </h1>

        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px' }}>
          {/* Letters List */}
          <div>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>All Documents ({letters.length})</h2>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                  Consultation letters & radiology referrals
                </p>
              </div>
              <div style={{ maxHeight: '700px', overflowY: 'auto' }}>
                {letters.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                    No letters generated yet
                  </div>
                ) : (
                  groupedLetters.map(group => (
                    <React.Fragment key={group.key}>
                      <div style={{
                        padding: '8px 24px',
                        backgroundColor: '#f3f4f6',
                        borderTop: '1px solid #e5e7eb',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '11px', fontWeight: '600', color: '#4b5563',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        position: 'sticky', top: 0, zIndex: 1
                      }}>
                        {group.label}
                      </div>
                      {group.letters.map((letter, index) => (
                    <button
                      key={letter.id}
                      onClick={() => viewLetter(letter.id)}
                      style={{
                        width: '100%', padding: '16px 24px', textAlign: 'left',
                        backgroundColor: selectedLetter?.letter.id === letter.id ? '#eff6ff' : 'white',
                        border: 'none',
                        borderTop: index > 0 ? '1px solid #e5e7eb' : 'none',
                        cursor: 'pointer', transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLetter?.letter.id !== letter.id) e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLetter?.letter.id !== letter.id) e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827', margin: 0 }}>
                            {letter.patient?.name || 'Unknown Patient'}
                          </p>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                            {new Date(letter.generatedAt).toLocaleDateString('en-NZ')}
                          </p>
                          <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', fontSize: '10px', fontWeight: '500', borderRadius: '4px',
                              backgroundColor: letter.letterType === 'mri_referral' ? '#dbeafe' : '#f3e8ff',
                              color: letter.letterType === 'mri_referral' ? '#1e40af' : '#7c3aed'
                            }}>
                              {letter.letterType === 'mri_referral' ? 'Radiology Referral' : 'Consultation'}
                            </span>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', fontSize: '10px', fontWeight: '500', borderRadius: '4px',
                              backgroundColor: getStatusColor(letter.status).bg,
                              color: getStatusColor(letter.status).text
                            }}>
                              {letter.status.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            {getGensolveBadge(letter.gensolveUploadStatus) && (
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', fontSize: '10px', fontWeight: '500', borderRadius: '4px',
                                backgroundColor: getGensolveBadge(letter.gensolveUploadStatus).bg,
                                color: getGensolveBadge(letter.gensolveUploadStatus).text
                              }}>
                                {getGensolveBadge(letter.gensolveUploadStatus).label}
                              </span>
                            )}
                          </div>
                        </div>
                        <FileText style={{ width: '20px', height: '20px', color: letter.letterType === 'mri_referral' ? '#3b82f6' : '#9ca3af', flexShrink: 0 }} />
                      </div>
                    </button>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Letter Detail */}
          <div>
            {selectedLetter ? (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{
                  padding: '16px 24px', borderBottom: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: '12px'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                        {selectedLetter.patient.name}
                      </h2>
                      <span style={{
                        padding: '2px 10px', fontSize: '12px', fontWeight: '500', borderRadius: '4px',
                        backgroundColor: selectedLetter.letter.letterType === 'mri_referral' ? '#dbeafe' : '#f3e8ff',
                        color: selectedLetter.letter.letterType === 'mri_referral' ? '#1e40af' : '#7c3aed'
                      }}>
                        {selectedLetter.letter.letterType === 'mri_referral' ? 'Radiology Referral' : 'Consultation Letter'}
                      </span>
                    </div>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                      Generated: {new Date(selectedLetter.letter.generatedAt).toLocaleString('en-NZ')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {!editing && isDoctor && selectedLetter.letter.letterType === 'initial' && !['sent', 'ready_to_send'].includes(selectedLetter.letter.status) && (
                      <>
                        <button
                          onClick={regenerateLetter}
                          disabled={regenerating}
                          title="Regenerate the letter with the current information"
                          style={{
                            padding: '8px 16px',
                            backgroundColor: regenerating ? '#94a3b8' : '#7c3aed',
                            color: 'white', borderRadius: '8px', border: 'none',
                            cursor: regenerating ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => { if (!regenerating) e.currentTarget.style.backgroundColor = '#6d28d9'; }}
                          onMouseLeave={(e) => { if (!regenerating) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
                        >
                          <RefreshCw style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          {regenerating ? 'Regenerating…' : 'Regenerate'}
                        </button>
                      </>
                    )}
                    {selectedLetter.letter.letterType === 'mri_referral' && (
                      <>
                        {/* Dropdown stays visible during editing so the doctor can
                            pick the practice before approving — the choice is saved
                            on change and prepended to the Gensolve upload PDF. */}
                        <select
                          value={radiologyProvider}
                          onChange={(e) => handleRadiologyProviderChange(e.target.value)}
                          title="Radiology practice for this MRI — appears on the Gensolve upload"
                          style={{
                            padding: '8px 10px', borderRadius: '8px',
                            border: '1px solid #cbd5e1', backgroundColor: 'white',
                            fontSize: '14px', cursor: 'pointer'
                          }}
                        >
                          <option value="">Select radiology…</option>
                          {RADIOLOGY_PROVIDERS.map(p => (
                            <option key={p.key} value={p.key}>{p.name}</option>
                          ))}
                        </select>
                        {/* The "Email to Provider" button uses the stored letter, not
                            the in-progress edits, so it's hidden during editing to
                            avoid emailing a stale version. */}
                        {!editing && (
                          <button
                            onClick={emailToRadiology}
                            disabled={!radiologyProvider || emailingRadiology}
                            title={radiologyProvider ? 'Email this MRI request to the selected provider' : 'Pick a radiology provider first'}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: radiologyProvider && !emailingRadiology ? '#0891b2' : '#94a3b8',
                              color: 'white', borderRadius: '8px', border: 'none',
                              cursor: radiologyProvider && !emailingRadiology ? 'pointer' : 'not-allowed',
                              display: 'flex', alignItems: 'center', fontSize: '14px'
                            }}
                            onMouseEnter={(e) => { if (radiologyProvider && !emailingRadiology) e.currentTarget.style.backgroundColor = '#0e7490'; }}
                            onMouseLeave={(e) => { if (radiologyProvider && !emailingRadiology) e.currentTarget.style.backgroundColor = '#0891b2'; }}
                          >
                            <Send style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                            {emailingRadiology ? 'Sending…' : 'Email to Provider'}
                          </button>
                        )}
                      </>
                    )}
                    {!editing && (
                      <>
                        <button
                          onClick={downloadWord}
                          style={{
                            padding: '8px 16px', backgroundColor: '#16a34a', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                        >
                          <Download style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Word (.docx)
                        </button>
                        <button
                          onClick={downloadPdf}
                          style={{
                            padding: '8px 16px', backgroundColor: '#2563eb', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                        >
                          <Download style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          PDF
                        </button>
                        <button
                          onClick={printLetter}
                          style={{
                            padding: '8px 16px', backgroundColor: '#7c3aed', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d28d9'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                        >
                          <Printer style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Print
                        </button>
                        <button
                          onClick={copyToClipboard}
                          style={{
                            padding: '8px 16px', backgroundColor: copied ? '#10b981' : '#4b5563', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                        >
                          {copied ? <Check style={{ width: '16px', height: '16px', marginRight: '8px' }} /> : <Copy style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                        <button
                          onClick={deleteLetter}
                          disabled={deleting}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: deleting ? '#9ca3af' : '#dc2626', color: 'white',
                            borderRadius: '8px', border: 'none',
                            cursor: deleting ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#b91c1c')}
                          onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#dc2626')}
                        >
                          <Trash2 style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div style={{ padding: '24px' }}>
                  {/* Patient info bar — quick-reference details for the reviewer
                      (phone for calling during review, ACC# / NHI / DOI cross-check) */}
                  {(() => {
                    const p = selectedLetter.patient || {};
                    const ref = selectedLetter.letter?.referral || {};
                    const formData = selectedLetter.letter?.form?.clinicalData || {};
                    const refDoi = ref.injuryDate || '';
                    const formDoi = formData.injuryDate || formData.problemDate || '';
                    // Day-first aware (DD/MM/YYYY) — see utils/formatNZDate.
                    const fmt = formatNZDate;
                    const norm = normalizeNZDateISO;
                    const doiMismatch = refDoi && formDoi && norm(refDoi) !== norm(formDoi);
                    const accLooksLikeNhi = p.accNumber && /^[A-HJ-NP-Z]{3}(?:\d{4}|\d{2}[A-HJ-NP-Z]{2})$/i.test(String(p.accNumber).replace(/\s+/g, '').trim());
                    const flagStyle = { fontSize: '11px', fontWeight: '600', color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px' };
                    const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' };
                    const valueStyle = { fontSize: '14px', color: '#111827', fontWeight: '500' };
                    return (
                      <div style={{
                        marginBottom: '16px', padding: '12px 16px',
                        backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0',
                        display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center'
                      }}>
                        <div>
                          <div style={labelStyle}>Phone</div>
                          <div style={valueStyle}>
                            {p.phone ? (
                              <a href={`tel:${p.phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{p.phone}</a>
                            ) : '—'}
                          </div>
                        </div>
                        <div>
                          <div style={labelStyle}>NHI</div>
                          <div style={valueStyle}>{p.nhi || '—'}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>ACC #</div>
                          <div style={valueStyle}>
                            {p.accNumber || '—'}
                            {accLooksLikeNhi && (
                              <span style={flagStyle} title="ACC numbers start with 2 letters; this looks like an NHI (3 letters). Confirm with the referrer.">⚠ looks like NHI</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={labelStyle}>DOI (referral)</div>
                          <div style={valueStyle}>
                            {fmt(refDoi) || '—'}
                            {doiMismatch && (
                              <span style={flagStyle} title={`Patient form date is ${fmt(formDoi)} — they disagree. Referral date is authoritative for ACC.`}>⚠ form says {fmt(formDoi)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {editing ? (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                        <button
                          onClick={() => setFullscreen(true)}
                          title="Expand to fullscreen"
                          style={{
                            padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151',
                            borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        >
                          <Maximize2 size={14} />
                          Expand
                        </button>
                      </div>
                      {selectedLetter.letter.letterType !== 'mri_referral' && (
                        <div style={{
                          padding: '16px 20px', marginBottom: '8px',
                          backgroundColor: '#fafafa', borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <PatientInfoBlock patient={selectedLetter.patient} letter={selectedLetter.letter} onPatientChange={handlePatientChange} onReferralChange={handleReferralChange} />
                        </div>
                      )}
                      <RichTextEditor
                        content={editedHtml}
                        onChange={setEditedHtml}
                        editable={true}
                      />
                      <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                        <button
                          onClick={saveEdit}
                          style={{
                            padding: '8px 24px', backgroundColor: '#16a34a', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                        >
                          <Check style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Save Changes
                        </button>
                        {isDoctor && (
                          <button
                            onClick={handleSaveAndApprove}
                            title={selectedLetter.letter.letterType === 'mri_referral' && radiologyProvider ? 'Save, approve, and email the MRI to the selected provider' : undefined}
                            style={{
                              padding: '8px 24px', backgroundColor: '#059669', color: 'white',
                              borderRadius: '8px', border: 'none', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', fontSize: '14px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                          >
                            <Check style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                            {selectedLetter.letter.letterType === 'mri_referral' && radiologyProvider ? 'Save, Approve & Email' : 'Save & Approve'}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditing(false);
                            setFullscreen(false);
                            const html = selectedLetter.letter.contentHtml || plainTextToHtml(selectedLetter.letter.content);
                            setEditedHtml(html);
                          }}
                          style={{
                            padding: '8px 24px', backgroundColor: '#e5e7eb', color: '#374151',
                            borderRadius: '8px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        >
                          <X style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* For MRI referrals, show the chosen radiology provider as a "To:" header above the content */}
                      {selectedLetter.letter.letterType === 'mri_referral' && radiologyProvider && (() => {
                        const provider = RADIOLOGY_PROVIDERS.find(p => p.key === radiologyProvider);
                        return provider ? (
                          <div style={{
                            marginBottom: '8px', padding: '8px 16px',
                            backgroundColor: '#ecfeff', borderRadius: '8px', border: '1px solid #a5f3fc',
                            fontSize: '14px', fontWeight: '600', color: '#155e75'
                          }}>
                            To: {provider.name}
                          </div>
                        ) : null;
                      })()}
                      {/* Render HTML content or fall back to plain text */}
                      {selectedLetter.letter.contentHtml ? (
                        <div
                          style={{
                            backgroundColor: '#fafafa', padding: '32px', borderRadius: '8px',
                            border: '1px solid #e5e7eb', maxHeight: '600px', overflowY: 'auto',
                            fontSize: '14px', lineHeight: '1.8', fontFamily: 'Arial, sans-serif', color: '#111827'
                          }}
                          className="rich-text-content"
                        >
                          {selectedLetter.letter.letterType !== 'mri_referral' && (
                            <PatientInfoBlock patient={selectedLetter.patient} letter={selectedLetter.letter} onPatientChange={handlePatientChange} onReferralChange={handleReferralChange} />
                          )}
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedLetter.letter.contentHtml) }} />
                        </div>
                      ) : (
                        <div style={{
                          backgroundColor: '#fafafa', padding: '32px', borderRadius: '8px',
                          border: '1px solid #e5e7eb', maxHeight: '600px', overflowY: 'auto'
                        }}>
                          {selectedLetter.letter.letterType !== 'mri_referral' && (
                            <PatientInfoBlock patient={selectedLetter.patient} letter={selectedLetter.letter} onPatientChange={handlePatientChange} onReferralChange={handleReferralChange} />
                          )}
                          <pre style={{
                            whiteSpace: 'pre-wrap', fontFamily: 'Arial, sans-serif',
                            fontSize: '14px', lineHeight: '1.8', margin: 0, color: '#111827'
                          }}>
                            {selectedLetter.letter.content}
                          </pre>
                        </div>
                      )}

                      {/* Recipients display */}
                      {selectedLetter.letter.recipients && selectedLetter.letter.recipients.length > 0 && (
                        <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                          <p style={{ fontSize: '12px', fontWeight: '600', color: '#0369a1', margin: '0 0 8px', textTransform: 'uppercase' }}>Recipients</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {selectedLetter.letter.recipients.map((r, i) => (
                              <span key={i} style={{
                                padding: '3px 10px', backgroundColor: 'white', borderRadius: '6px',
                                fontSize: '12px', color: '#334155', border: '1px solid #e2e8f0'
                              }}>
                                {r.name || r.email}
                                {r.sendStatus === 'sent' && <Check size={12} style={{ marginLeft: '4px', color: '#16a34a', verticalAlign: 'middle' }} />}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Status Actions */}
                {!editing && (
                  <div style={{
                    padding: '16px 24px', borderTop: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px'
                  }}>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px', marginTop: 0 }}>
                      Letter Status: <span style={{
                        color: getStatusColor(selectedLetter.letter.status).text,
                        backgroundColor: getStatusColor(selectedLetter.letter.status).bg,
                        padding: '2px 8px', borderRadius: '4px'
                      }}>
                        {selectedLetter.letter.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {/* Doctor/admin: Approve on draft/pending_review/reviewed letters */}
                      {isDoctor && !['sent', 'ready_to_send', 'approved'].includes(selectedLetter.letter.status) && (
                        <button
                          onClick={() => updateStatus('approved')}
                          style={{
                            padding: '8px 16px', backgroundColor: '#16a34a', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px',
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                        >
                          <Check size={16} />
                          Approve Letter
                        </button>
                      )}
                      {/* Both roles: Add Recipients & Send on approved letters */}
                      {selectedLetter.letter.status === 'approved' && (
                        <button
                          onClick={handleReadyToSend}
                          style={{
                            padding: '8px 16px', backgroundColor: '#2563eb', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px',
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                        >
                          <Send size={16} />
                          Add Recipients & Send
                        </button>
                      )}
                      {/* Doctor/admin only: Needs Review to un-approve */}
                      {isDoctor && !['sent', 'pending_review'].includes(selectedLetter.letter.status) && (
                        <button
                          onClick={() => updateStatus('pending_review')}
                          style={{
                            padding: '8px 16px', backgroundColor: '#ca8a04', color: 'white',
                            borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#a16207'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ca8a04'}
                        >
                          Needs Review
                        </button>
                      )}
                      {selectedLetter.letter.status === 'sent' && (
                        <span style={{ padding: '8px 16px', fontSize: '14px', color: '#065f46', fontWeight: '500' }}>
                          Sent {selectedLetter.letter.sentAt ? `on ${new Date(selectedLetter.letter.sentAt).toLocaleString('en-NZ')}` : ''}
                        </span>
                      )}
                      {/* Gensolve upload status */}
                      {getGensolveBadge(selectedLetter.letter.gensolveUploadStatus) && (
                        <span style={{
                          padding: '6px 12px', fontSize: '13px', fontWeight: '500', borderRadius: '6px',
                          backgroundColor: getGensolveBadge(selectedLetter.letter.gensolveUploadStatus).bg,
                          color: getGensolveBadge(selectedLetter.letter.gensolveUploadStatus).text
                        }}>
                          {getGensolveBadge(selectedLetter.letter.gensolveUploadStatus).label}
                        </span>
                      )}
                      {selectedLetter.letter.gensolveUploadStatus === 'failed' && (
                        <button
                          onClick={() => retryGensolveUpload(selectedLetter.letter.id)}
                          disabled={retryingGensolve}
                          style={{
                            padding: '6px 14px', backgroundColor: retryingGensolve ? '#9ca3af' : '#ea580c', color: 'white',
                            borderRadius: '8px', border: 'none',
                            cursor: retryingGensolve ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', fontSize: '13px', gap: '6px'
                          }}
                          onMouseEnter={(e) => !retryingGensolve && (e.currentTarget.style.backgroundColor = '#c2410c')}
                          onMouseLeave={(e) => !retryingGensolve && (e.currentTarget.style.backgroundColor = '#ea580c')}
                        >
                          <RefreshCw size={14} style={retryingGensolve ? { animation: 'spin 1s linear infinite' } : {}} />
                          {retryingGensolve ? 'Retrying...' : 'Retry Gensolve'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white', borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '48px', textAlign: 'center'
              }}>
                <FileText style={{ width: '64px', height: '64px', color: '#d1d5db', margin: '0 auto 16px' }} />
                <p style={{ color: '#6b7280', margin: 0 }}>Select a letter to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Editor Overlay */}
      {fullscreen && editing && selectedLetter && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1000, backgroundColor: 'white',
          display: 'flex', flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 24px', borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setFullscreen(false)}
                title="Exit fullscreen"
                style={{
                  padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151',
                  borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                <Minimize2 size={14} />
                Minimize
              </button>
              <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                {selectedLetter.patient.name}
              </h2>
              <span style={{
                padding: '2px 10px', fontSize: '12px', fontWeight: '500', borderRadius: '4px',
                backgroundColor: selectedLetter.letter.letterType === 'mri_referral' ? '#dbeafe' : '#f3e8ff',
                color: selectedLetter.letter.letterType === 'mri_referral' ? '#1e40af' : '#7c3aed'
              }}>
                {selectedLetter.letter.letterType === 'mri_referral' ? 'Radiology Referral' : 'Consultation Letter'}
              </span>
            </div>
          </div>

          {/* Editor */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px 48px' }}>
            {selectedLetter.letter.letterType !== 'mri_referral' && (
              <div style={{
                padding: '16px 20px', marginBottom: '16px',
                backgroundColor: '#fafafa', borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <PatientInfoBlock patient={selectedLetter.patient} letter={selectedLetter.letter} onPatientChange={handlePatientChange} onReferralChange={handleReferralChange} />
              </div>
            )}
            <RichTextEditor
              content={editedHtml}
              onChange={setEditedHtml}
              editable={true}
              maxHeight="none"
            />
          </div>

          {/* Footer */}
          <div style={{
            padding: '12px 24px', borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: '16px', flexShrink: 0, backgroundColor: '#f9fafb'
          }}>
            <button
              onClick={saveEdit}
              style={{
                padding: '8px 24px', backgroundColor: '#16a34a', color: 'white',
                borderRadius: '8px', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', fontSize: '14px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
            >
              <Check style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              Save Changes
            </button>
            {isDoctor && (
              <button
                onClick={handleSaveAndApprove}
                title={selectedLetter.letter.letterType === 'mri_referral' && radiologyProvider ? 'Save, approve, and email the MRI to the selected provider' : undefined}
                style={{
                  padding: '8px 24px', backgroundColor: '#059669', color: 'white',
                  borderRadius: '8px', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', fontSize: '14px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              >
                <Check style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                {selectedLetter.letter.letterType === 'mri_referral' && radiologyProvider ? 'Save, Approve & Email' : 'Save & Approve'}
              </button>
            )}
            <button
              onClick={() => {
                setEditing(false);
                setFullscreen(false);
                const html = selectedLetter.letter.contentHtml || plainTextToHtml(selectedLetter.letter.content);
                setEditedHtml(html);
              }}
              style={{
                padding: '8px 24px', backgroundColor: '#e5e7eb', color: '#374151',
                borderRadius: '8px', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', fontSize: '14px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
            >
              <X style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Recipient Selector Modal */}
      {showRecipientSelector && selectedLetter && (
        <RecipientSelector
          letter={selectedLetter.letter}
          patient={selectedLetter.patient}
          onConfirm={confirmReadyToSend}
          onCancel={() => setShowRecipientSelector(false)}
        />
      )}

      <style>{`
        .rich-text-content h3 { font-size: 15px; font-weight: 700; margin: 12px 0 6px; }
        .rich-text-content p { margin: 0 0 8px; }
        .rich-text-content ul, .rich-text-content ol { padding-left: 24px; margin: 4px 0 8px; }
        .rich-text-content li { margin-bottom: 2px; }
      `}</style>
    </div>
  );
}

export default LettersPage;
